import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React from 'react';
import { Cast } from 'lucide-react';
import { SubtitleTrack } from '@/store/videoFeatures/types/video-features';
import { cn } from '@/lib/utils';
import { VideoFeatures } from '@/store/videoFeatures';
import { PlayButton } from './ui/PlayButton';
import { VolumeControl } from './ui/VolumeControl';
import { ControlButton } from './ui/ControlButton';
import { TimeDisplay } from './ui/TimeDisplay';
import { ProgressTrack } from './ui/ProgressTrack';
import { EmbeddedProgressTrack } from './ui/EmbeddedProgressTrack';
import { PlaybackSpeedMenu } from './ui/PlaybackSpeedMenu';
import { SettingsMenu } from './ui/SettingsMenu';
import { ThemedIcon } from './ui/ThemedIcon';

interface VideoControlsProps {
  features: VideoFeatures;
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  volume: number;
  isMuted: boolean;
  isVertical?: boolean;
  showControls: boolean;
  togglePlay: () => void;
  handleSkipBack: () => void;
  handleSkipForward: () => void;
  toggleMute: () => void;
  handleVolumeChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSeek: (e: React.MouseEvent<HTMLDivElement>) => void;
  toggleFullscreen: () => void;
  togglePictureInPicture: () => void;
  onTvCast?: () => void;
  setPlaybackSpeed: (speed: number) => void;
  formatTime: (time: number) => string;
  subtitles?: SubtitleTrack[];
  subtitleVisible?: boolean;
  activeSrclang?: string | null;
  onToggleSubtitle?: () => void;
  onSelectSubtitle?: (srclang: string) => void;
}

export const VideoControls: React.FC<VideoControlsProps> = ({
  features,
  isPlaying,
  currentTime,
  duration,
  volume,
  isMuted,
  isVertical = false,
  showControls,
  togglePlay,
  handleSkipBack,
  handleSkipForward,
  toggleMute,
  handleVolumeChange,
  handleSeek,
  toggleFullscreen,
  togglePictureInPicture,
  onTvCast,
  setPlaybackSpeed,
  formatTime,
  subtitles,
  subtitleVisible,
  activeSrclang,
  onToggleSubtitle,
  onSelectSubtitle
}) => {
  useLanguage();
  const [showSubMenu, setShowSubMenu] = React.useState(false);
  // Tema dos controles — afeta TODOS os ícones do player. Cada tema usa uma
  // família de SVGs diferente (Lucide outline / Phosphor fill / duotone / bold).
  // 'custom' é o id legado do tema Safari (upload de ícone descontinuado) —
  // normalizado pra 'safari' pra manter compat com vídeos já salvos.
  const rawControlsTheme = features.controlsTheme === 'custom' ? 'safari' : features.controlsTheme;
  const controlsTheme = rawControlsTheme &&
    ['minimal', 'solid', 'duotone', 'bold', 'safari'].includes(rawControlsTheme) ?
    rawControlsTheme as 'minimal' | 'solid' | 'duotone' | 'bold' | 'safari' :
    'minimal';

  // Use new playerColors system with fallback to controlsColor
  // controlColor = cor dos ícones dos controles (play pequeno, volume, fullscreen, etc)
  const primaryColor = features.controlColor || features.playerColors?.primary || features.controlsColor || '#FFFFFF';
  const secondaryColor = features.playerColors?.secondary || '#64748B';

  // Aplicar tema personalizado - APENAS quando playerThemeEnabled estiver ativo
  const playerTheme = features.playerThemeEnabled ? features.playerColors : null;

  // Em vídeos verticais com os 3 controles do cluster direito ativos
  // (velocidade + PiP + fullscreen), o tempo total quebra o layout — mostra
  // somente o tempo atual nesse caso. PiP só conta se o navegador suportar
  // (mesma condição usada para renderizar o botão).
  const pipIconVisible = features.pictureInPicture &&
    typeof document !== 'undefined' &&
    'pictureInPictureEnabled' in document &&
    document.pictureInPictureEnabled;
  const showDurationInTime = !(
    isVertical &&
    features.playbackSpeedControl &&
    pipIconVisible &&
    features.fullScreen
  );

  // Cluster direito: quando há mais de 2 ícones visíveis (entre velocidade,
  // PiP, cast e tela cheia), condensa tudo numa engrenagem pra economizar
  // espaço. Cast só conta se o browser suportar AirPlay ou Remote Playback.
  const castSupported = features.tvCastEnabled && !!onTvCast && typeof window !== 'undefined' && (() => {
    const w = window as unknown as { WebKitPlaybackTargetAvailabilityEvent?: unknown };
    return typeof w.WebKitPlaybackTargetAvailabilityEvent !== 'undefined' || 'RemotePlayback' in window;
  })();
  const rightVisibleCount =
    (features.playbackSpeedControl ? 1 : 0) +
    (pipIconVisible ? 1 : 0) +
    (castSupported ? 1 : 0) +
    (features.fullScreen ? 1 : 0);
  const useSettingsGear = rightVisibleCount > 2;

  // Converter hex para rgba com opacidade
  const hexToRgba = (hex: string, opacity: number): string => {
    // Se já for rgba, retorna como está
    if (hex.startsWith('rgba') || hex.startsWith('rgb')) {
      return hex;
    }

    // Remove # se presente
    const cleanHex = hex.replace('#', '');
    const r = parseInt(cleanHex.slice(0, 2), 16);
    const g = parseInt(cleanHex.slice(2, 4), 16);
    const b = parseInt(cleanHex.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  };

  // Cor de fundo da barra: prioriza controlBarColor (rgba salvo pelo novo
  // botão), com fallback para playerColors.backgroundColor + opacity (legado).
  const backgroundColorWithOpacity = features.playerThemeEnabled
    ? (features.controlBarColor
        || (playerTheme?.backgroundColor
            ? hexToRgba(playerTheme.backgroundColor, playerTheme.opacity || 0.45)
            : null))
    : null;

  // Quando o tema está ativo e controlBarEnabled = false, escondemos a barra
  // inferior (mas mantemos middle/big-play e overlays via outros componentes).
  const hideControlBar = features.playerThemeEnabled && features.controlBarEnabled === false;

  // Duotone usa estética "YouTube": pílulas em torno de cada grupo de
  // controles, sem fundo cobrindo a barra inteira de ponta a ponta.
  const isDuotone = controlsTheme === 'duotone';
  // No tema Safari os skips vão pro cluster central (BigPlayButton) e o volume
  // ganha a barra (slider) no rodapé, ocupando o espaço que era dos skips.
  const isSafari = controlsTheme === 'safari';
  const pillBg = features.controlBarColor || 'rgba(0,0,0,0.5)';
  const pillClass = 'flex items-center rounded-full px-2 h-10 backdrop-blur-sm';

  return (
    <div
      className={cn(
        "absolute bottom-0 left-0 w-full transition-all duration-300 ease-in-out",
        showControls ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2 pointer-events-none",
        (features.disableControls || hideControlBar) && "hidden"
      )}
    >
      {/* Background layer with opacity — suprimido em duotone (cada pílula
          tem seu próprio fundo). */}
      {features.playerThemeEnabled && backgroundColorWithOpacity && !isDuotone && (
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: backgroundColorWithOpacity,
          }}
        />
      )}

      {/* Controls content layer */}
      <div
        className={cn(
          "relative z-10 px-4 py-2",
          // Em duotone não aplica gradiente — pílulas dão o destaque visual.
          !isDuotone && (!features.playerThemeEnabled || !backgroundColorWithOpacity) && "bg-gradient-to-t from-black/70 to-transparent"
        )}
        style={{
          color: primaryColor,
          borderRadius: features.playerThemeEnabled && !isDuotone ? `${playerTheme?.borderRadius || 8}px` : '0px',
        }}
      >
      {/* Progress bar - only render when player theme is disabled */}
      {features.showProgressBar && (
        <ProgressTrack 
          currentTime={currentTime}
          duration={duration}
          onSeek={handleSeek}
          progressBarStyle={features.progressBarStyle}
        />
      )}
      
      {/* Control buttons row */}
      <div className={cn(
        "flex items-center",
        features.playerThemeEnabled && features.showProgressBar
          ? "justify-between"
          : "justify-between"
      )}>
        <div className="flex items-center space-x-1">
          {/* Grupo PLAY — pílula em duotone */}
          {(isPlaying || features.smallPlayButton) && (
            <div className={isDuotone ? pillClass : ''} style={isDuotone ? { background: pillBg } : undefined}>
              <PlayButton
                isPlaying={isPlaying}
                onClick={togglePlay}
                iconType={controlsTheme}
                small={features.smallPlayButton}
                color={primaryColor}
              />
            </div>
          )}

          {/* Grupo SKIP — pílula única envolvendo skipBack + skipForward. No
              tema Safari os skips vão pro cluster central, então some daqui. */}
          {!isSafari && (features.rewind || features.skipForward) && (
            <div className={isDuotone ? `${pillClass} gap-1` : 'flex items-center space-x-3'} style={isDuotone ? { background: pillBg } : undefined}>
              {features.rewind && (
                <ControlButton onClick={handleSkipBack} color={primaryColor} theme={controlsTheme}>
                  <ThemedIcon name="skipBack" theme={controlsTheme} className="h-5 w-5" />
                </ControlButton>
              )}
              {features.skipForward && (
                <ControlButton onClick={handleSkipForward} color={primaryColor} theme={controlsTheme}>
                  <ThemedIcon name="skipForward" theme={controlsTheme} className="h-5 w-5" />
                </ControlButton>
              )}
            </div>
          )}

          {/* Grupo VOLUME — pílula em duotone */}
          {features.volumeControl && (
            <div className={isDuotone ? pillClass : ''} style={isDuotone ? { background: pillBg } : undefined}>
              <VolumeControl
                volume={volume}
                isMuted={isMuted}
                onToggleMute={toggleMute}
                onVolumeChange={handleVolumeChange}
                color={primaryColor}
                theme={controlsTheme}
                showSlider={isSafari}
              />
            </div>
          )}

          {/* Embedded progress bar when theme is enabled - positioned BEFORE time */}
          {features.showProgressBar && features.playerThemeEnabled && (
            <EmbeddedProgressTrack
              currentTime={currentTime}
              duration={duration}
              onSeek={handleSeek}
              progressBarStyle={features.progressBarStyle}
              color={primaryColor}
            />
          )}

          {/* Grupo TEMPO — pílula em duotone */}
          {features.showCurrentTime && (
            <div className={isDuotone ? `${pillClass} px-3` : ''} style={isDuotone ? { background: pillBg } : undefined}>
              <TimeDisplay
                currentTime={currentTime}
                duration={duration}
                showDuration={showDurationInTime}
                formatTime={formatTime}
                color={primaryColor}
              />
            </div>
          )}
        </div>

        {/* Cluster DIREITO — pílula única em duotone */}
        <div
          className={cn(
            'flex items-center',
            isDuotone ? `${pillClass} gap-1` : 'space-x-2'
          )}
          style={isDuotone ? { background: pillBg } : undefined}
        >
          {useSettingsGear ? (
            // Mais de 2 ícones no cluster direito — agrupa tudo numa engrenagem
            // pra economizar espaço.
            <SettingsMenu
              speedEnabled={!!features.playbackSpeedControl}
              currentSpeed={features.playbackSpeed || 1}
              onSpeedChange={setPlaybackSpeed}
              pipEnabled={!!pipIconVisible}
              onTogglePip={togglePictureInPicture}
              castEnabled={castSupported}
              onTvCast={onTvCast}
              fullscreenEnabled={!!features.fullScreen}
              onToggleFullscreen={toggleFullscreen}
              color={primaryColor}
              theme={controlsTheme}
            />
          ) : (
            <>
              {features.playbackSpeedControl && (
                <PlaybackSpeedMenu
                  currentSpeed={features.playbackSpeed || 1}
                  onSpeedChange={setPlaybackSpeed}
                  color={primaryColor}
                />
              )}

              {pipIconVisible && (
                <ControlButton onClick={togglePictureInPicture} color={primaryColor} theme={controlsTheme}>
                  <ThemedIcon name="pip" theme={controlsTheme} className="h-5 w-5" />
                </ControlButton>
              )}

              {castSupported && onTvCast && (
                <ControlButton onClick={onTvCast} color={primaryColor} theme={controlsTheme}>
                  <Cast className="h-5 w-5" />
                </ControlButton>
              )}

              {features.fullScreen && (
                <ControlButton onClick={toggleFullscreen} color={primaryColor} theme={controlsTheme}>
                  <ThemedIcon name="fullscreen" theme={controlsTheme} className="h-5 w-5" />
                </ControlButton>
              )}
            </>
          )}

          {/* Subtitle (CC) button */}
          {subtitles && subtitles.length > 0 && onToggleSubtitle && (
            <div className="relative">
              <button
                onClick={() => {
                  if (subtitles.length === 1) {
                    onToggleSubtitle();
                  } else {
                    setShowSubMenu(v => !v);
                  }
                }}
                className="flex items-center justify-center hover:opacity-80 transition-opacity"
                style={{ color: primaryColor }}
                title={translateInterfaceText("Legendas")}
              >
                <ThemedIcon
                  name="captions"
                  theme={controlsTheme}
                  className="h-4 w-4"
                  style={{ opacity: subtitleVisible ? 1 : 0.45 }}
                />
              </button>
              {showSubMenu && subtitles.length > 1 && (
                <div className="absolute bottom-8 right-0 bg-black/90 rounded-lg py-1 min-w-[120px] z-50 shadow-lg">
                  <button
                    className="w-full text-left px-3 py-1.5 text-xs hover:bg-white/10 transition-colors"
                    style={{ color: subtitleVisible ? '#fff' : 'rgba(255,255,255,0.45)' }}
                    onClick={() => { onToggleSubtitle(); setShowSubMenu(false); }}
                  >
                    {subtitleVisible ? translateInterfaceText("Desativar") : translateInterfaceText("Ativar")}
                  </button>
                  <div className="h-px bg-white/10 my-1" />
                  {subtitles.map(sub => (
                    <button
                      key={sub.srclang}
                      className="w-full text-left px-3 py-1.5 text-xs hover:bg-white/10 transition-colors"
                      style={{ color: activeSrclang === sub.srclang ? primaryColor : 'rgba(255,255,255,0.7)' }}
                      onClick={() => { onSelectSubtitle?.(sub.srclang); setShowSubMenu(false); }}
                    >
                      {sub.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      </div>
    </div>
  );
};